import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { UserAvatar } from "@/components/user-avatar";
import { FollowButton } from "@/components/follow-button";
import { AccountBadge } from "@/components/account-badge";
import type { PersonSummary } from "@/lib/follows";

/**
 * Editorial list of people — no card per row, just a clean line with the real
 * profile photo, the name, and a live follow control.
 */
export function PeopleList({
  people,
  followingIds,
  empty = "Nobody here yet.",
  onFollowChange,
}: {
  people: PersonSummary[];
  followingIds: Set<string>;
  empty?: string;
  onFollowChange?: (id: string, following: boolean) => void;
}) {
  if (people.length === 0) {
    return <p className="py-10 text-center text-sm text-muted-foreground">{empty}</p>;
  }
  return (
    <ul>
      {people.map((p, i) => (
        <motion.li
          key={p.id}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: Math.min(i, 8) * 0.02, ease: [0.22, 0.61, 0.36, 1] }}
        >
          <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 py-4">
            <Link to="/profile/$username" params={{ username: p.username }} className="shrink-0">
              <UserAvatar
                name={p.full_name}
                username={p.username}
                avatarUrl={p.avatar_url}
                className="h-11 w-11"
                size={44}
              />
            </Link>
            <Link to="/profile/$username" params={{ username: p.username }} className="min-w-0">
              <span className="flex min-w-0 items-center gap-1">
                <span className="truncate font-semibold">{p.full_name || p.username}</span>
                <AccountBadge accountType={p.account_type as never} verified={p.verified} />
              </span>
              <span className="block truncate text-[13px] text-muted-foreground">
                @{p.username}
              </span>
              {p.bio && (
                <span className="mt-1 line-clamp-2 block text-[13px] text-muted-foreground">
                  {p.bio}
                </span>
              )}
            </Link>
            <FollowButton
              targetId={p.id}
              size="sm"
              initialFollowing={followingIds.has(p.id)}
              onChange={(f) => onFollowChange?.(p.id, f)}
            />
          </div>
        </motion.li>
      ))}
    </ul>
  );
}
